import { Box, Card, Typography, Tabs, Tab, Paper, CircularProgress, Skeleton } from '@mui/material'
import Widget from '../components/widget/widget'
import Chart from '../components/chart/chart'
import StackedBarChart from '../components/chart/stackedBarChart'
import TopPromptsTable from '../components/tables/topPromptsTable'
import { useQuery } from '@apollo/client'
import { dashboardData, getToolUsageAnalytics } from '../graphql/queries'
import { Grid } from '@mui/material'
import { useState } from 'react'
import DateRangeSelector from '../components/dateRangeSelector'

// Helper function to calculate date range
const calculateDateRange = (period) => {
  let end = new Date()
  let start = new Date()

  switch (period) {
    case '7days':
      start.setDate(end.getDate() - 7)
      break
    case '28days':
      start.setDate(end.getDate() - 28)
      break
    case '30days':
      start.setDate(end.getDate() - 30)
      break
    case '90days':
      start.setDate(end.getDate() - 90)
      break
    case '12months':
      start.setDate(end.getDate() - 365)
      break
    case 'ytd':
      start = new Date(end.getFullYear(), 0, 1)
      break
    case 'lastyear':
      start = new Date(end.getFullYear() - 1, 0, 1)
      end = new Date(end.getFullYear() - 1, 11, 31)
      break
    case 'alltime':
      start = new Date('2020-01-01')
      break
    default:
      start.setDate(end.getDate() - 30)
  }

  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  }
}

// Initialize with last 30 days
const getInitialDateRange = () => calculateDateRange('30days')

export default function HomePage() {
  const [dateRange, setDateRange] = useState(getInitialDateRange)
  const [selectedPeriod, setSelectedPeriod] = useState('30days')
  const [activeTab, setActiveTab] = useState(0)

  const handlePeriodChange = (period) => {
    if (period === 'custom') return
    setSelectedPeriod(period)
    setDateRange(calculateDateRange(period))
  }

  const handleCustomDateChange = (start, end) => {
    setSelectedPeriod('custom')
    setDateRange({
      startDate: start || dateRange.startDate,
      endDate: end || dateRange.endDate,
    })
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  // Dashboard query
  const { 
    loading: dashboardLoading, 
    data: dashboardDataResult 
  } = useQuery(dashboardData)

  // Analytics query - use previousData to keep showing old data while loading
  const { 
    loading: analyticsLoading, 
    data: analyticsDataResult,
    previousData: analyticsPreviousData,
  } = useQuery(getToolUsageAnalytics, {
    variables: {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      limit: 10,
    },
    fetchPolicy: 'cache-and-network',
  })

  // Use previous data while new data loads - this prevents the UI flash
  const analyticsToUse = analyticsDataResult || analyticsPreviousData

  // Extract data
  const rowData = dashboardDataResult?.dashboardData || {}
  const dailyVideo = rowData?.dailyVideoCount
  const reversedArray = dailyVideo ? dailyVideo.slice().reverse() : []
  
  const analyticsData = analyticsToUse?.getToolUsageAnalytics || null
  const topPrompts = analyticsData?.topPrompts || []
  const promptUsageByDate = analyticsData?.promptUsageByDate || []
  const totalUsageCount = analyticsData?.totalUsageCount || 0
  const totalImageCount = analyticsData?.totalImageCount || 0

  // Only show skeleton on very first load (no data at all)
  const isInitialLoad = dashboardLoading && !dashboardDataResult

  // Show spinner when refetching analytics
  const isRefetching = analyticsLoading && analyticsToUse

  const containerWidth = { xs: '100%', md: '95%', lg: '92%' }

  if (isInitialLoad) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
        <Grid container spacing={2} sx={{ width: containerWidth }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={100} />
            </Grid>
          ))}
        </Grid>
        <Box sx={{ width: containerWidth }}>
          <Skeleton variant="rounded" height={300} />
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
      <Grid container spacing={2} sx={{ width: containerWidth }}>
        <Grid item xs={12} sm={6} md={3} sx={{ height: '100%' }}>
          <Widget type="users" amount={rowData.totalUser} />
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ height: '100%' }}>
          <Widget type="models" amount={rowData.activeSubscriptions} />
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ height: '100%' }}>
          <Widget type="predictions" amount={rowData.totalVideo} />
        </Grid>
        <Grid item xs={12} sm={6} md={3} sx={{ height: '100%' }}>
          <Widget type="sets" amount={rowData.modelCount} />
        </Grid>
      </Grid>

      <Box sx={{ width: containerWidth }}>
        <Chart
          title="Weekly Prediction Count"
          aspect={2 / 1}
          predictionData={reversedArray}
        />
      </Box>

      <Box sx={{ width: containerWidth }}>
        <Card sx={{ boxShadow: 3, borderRadius: 4, mb: 2 }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Tool Usage Analytics
              </Typography>
              {isRefetching && (
                <CircularProgress size={18} />
              )}
            </Box>
            <Box sx={{ minWidth: 250 }}>
              <DateRangeSelector
                selectedPeriod={selectedPeriod}
                onPeriodChange={handlePeriodChange}
                dateRange={dateRange}
                onCustomDateChange={handleCustomDateChange}
              />
            </Box>
          </Box>

          {/* Stat Cards */}
          <Grid container spacing={2} sx={{ px: 2, pb: 2 }}>
            <Grid item xs={12} sm={6}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  position: 'relative',
                }}
              >
                {isRefetching && (
                  <CircularProgress 
                    size={16} 
                    sx={{ position: 'absolute', top: 12, right: 12, color: 'rgba(255,255,255,0.7)' }} 
                  />
                )}
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  Total Tool Usage
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {totalUsageCount.toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, mt: 1 }}>
                  prompts executed
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white',
                  position: 'relative',
                }}
              >
                {isRefetching && (
                  <CircularProgress 
                    size={16} 
                    sx={{ position: 'absolute', top: 12, right: 12, color: 'rgba(255,255,255,0.7)' }} 
                  />
                )}
                <Typography variant="body2" sx={{ opacity: 0.9, mb: 1 }}>
                  Total Images Created
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {totalImageCount.toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8, mt: 1 }}>
                  images generated
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              px: 2,
            }}
          >
            <Tab label="Overview" />
            <Tab label="Chart View" />
            <Tab label="Table View" />
          </Tabs>
        </Card>
      </Box>

      {activeTab === 0 && (
        <>
          <Box sx={{ width: containerWidth }}>
            <StackedBarChart 
              title="Tool/Prompt Usage Timeline"
              data={promptUsageByDate}
              topPrompts={topPrompts}
            />
          </Box>
          <Box sx={{ width: containerWidth }}>
            <TopPromptsTable data={topPrompts} />
          </Box>
        </>
      )}

      {activeTab === 1 && (
        <Box sx={{ width: containerWidth }}>
          <StackedBarChart 
            title="Tool/Prompt Usage Timeline"
            data={promptUsageByDate}
            topPrompts={topPrompts}
          />
        </Box>
      )}

      {activeTab === 2 && (
        <Box sx={{ width: containerWidth }}>
          <TopPromptsTable data={topPrompts} />
        </Box>
      )}
    </Box>
  )
}
